import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

/**
 * A custom Events service just like Ionic 3 Events https://ionicframework.com/docs/v3/api/util/Events/ which got removed in Ionic 5.
 *
 * @author Shashank Agrawal
 */
@Injectable({
    providedIn: 'root'
})
export class EventsService {

    // observers
    private channels: { [key: string]: Subject<any>; } = {};
    // pending events
    private events: { [key: string]: Array<any>; } = {};

    /**
     * Subscribe to a topic and provide a single handler/observer.
     * @param topic The name of the topic to subscribe to.
     * @param observer The observer or callback function to listen when changes are published.
     *
     * @returns Subscription from which you can unsubscribe to release memory resources and to prevent memory leak.
     */
    subscribe(topic: string, observer: (_: any) => void): Subscription {
        // create observer list
        if (!this.channels[topic]) {
            this.channels[topic] = new Subject<any>();
        }

        // subscribe observer
        let res = this.channels[topic].subscribe(observer);

        // process pending events for this topic (if any)
        this.processPendingEvents(topic);

        return res;
    }

    /**
     * Publish some data to the subscribers of the given topic.
     * @param topic The name of the topic to emit data to.
     * @param data data in any format to pass on.
     */
    publish(topic: string, data: any): void {
        const subject = this.channels[topic];
        if (!subject || subject.observers.length == 0) { // no observers for this topic
            // Or you can create a new subject for future subscribers
            this.channels[topic] = new Subject<any>();
            // add event to pending list
            this.addPendingEvent(topic, data);
            return;
        }

        subject.next(data);
    }

    /**
     * When you are sure that you are done with the topic and the subscribers no longer needs to listen to a particular topic, you can
     * destroy the observable of the topic using this method.
     * @param topic The name of the topic to destroy.
     */
    destroy(topic: string): null {
        const subject = this.channels[topic];
        if (!subject) {
            return;
        }
        // process pending events before finishing
        this.processPendingEvents(topic);
        // finish up
        subject.complete();
        delete this.channels[topic];
    }

    /**
     * When an event is thrown but no observer is subscribed to it
     * we add it to a list of pending events for that specific topic
     * to be processed later when an observer is available.
     * @param topic Event name
     * @param data Event data
     */
    private addPendingEvent(topic: string, data: any){
        let events: Array<any> = this.events[topic];
        if (!events){
            events = this.events[topic] = new Array<any>();
        }
        events.push(data);
    }
    
    /**
     * Process all pending events for a specific topic
     * @param topic Event name
     */
    private processPendingEvents(topic: string): void {
        const subject = this.channels[topic];
        if (!subject || subject.observers.length == 0){
            return; // no observers available for this topic, avoid infinite loop
        }
        let events: Array<any> = this.events[topic];
        if (events){
            while (events.length > 0){
                let event = events.pop();
                this.publish(topic, event);
            }
        }
    }
}